import { TrackedBarcodeMode } from '../models/tracked-barcode.types'

export interface InputSourceConfig {
  maxIntervalMs: number
  minChars: number
}

const DEFAULTS: InputSourceConfig = { maxIntervalMs: 50, minChars: 3 }

const EDIT_KEYS = new Set([
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
])
const PASTE_LIKE = new Set([
  'insertFromPaste',
  'insertFromDrop',
  'insertFromYank',
  'insertReplacementText',
])

export class InputSourceDetector {
  private stamps: number[] = []
  private enterAt: number | null = null
  private edited = false
  private unknown = false

  constructor(private readonly config: InputSourceConfig = DEFAULTS) {}

  keydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      this.enterAt = e.timeStamp
      return
    }
    if (e.key === 'Unidentified') {
      this.unknown = true
      return
    }
    if (EDIT_KEYS.has(e.key)) {
      this.edited = true
      return
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return
    if (e.key.length === 1) {
      this.stamps.push(e.timeStamp)
      this.enterAt = null
    }
  }

  input(e: Event, value: string): void {
    if (value === '') {
      this.reset()
      return
    }
    const type = (e as InputEvent).inputType ?? ''
    if (type.startsWith('delete')) this.edited = true
    else if (PASTE_LIKE.has(type)) this.unknown = true
  }

  evaluate(
    value: string,
    endedWithEnter: boolean,
  ): TrackedBarcodeMode | undefined {
    if (this.unknown) return
    if (this.edited) return 'KEYBOARD'
    if (
      value.length !== this.stamps.length ||
      value.length < this.config.minChars
    ) {
      return
    }

    const enterOk = endedWithEnter && this.enterAt !== null
    const points = enterOk ? [...this.stamps, this.enterAt!] : this.stamps
    let maxGap = 0
    for (let i = 1; i < points.length; i++) {
      maxGap = Math.max(maxGap, points[i] - points[i - 1])
    }

    return enterOk && maxGap <= this.config.maxIntervalMs
      ? 'SCANNER'
      : 'KEYBOARD'
  }

  reset(): void {
    this.stamps = []
    this.enterAt = null
    this.edited = false
    this.unknown = false
  }
}
